import { Injectable } from '@angular/core';
import { AlarmFlags, ChannelFlags, ChannelStatus, FlagOption, SENSOR_FAILURE } from '../models/flags';
import { AlarmLevel, isDigital, isSystemChannelType } from '../models/channelType';

@Injectable({ providedIn: 'root' })
export class FlagsService {
    private flagValues: { [key: string]: ChannelFlags } = {};

    private channelLevel: { [key: string]: AlarmLevel } = {};
    private channelsLevels: { [key: string]: {} | string } = {};
    private channelStatus: { [key: string]: FlagOption } = {};
    private digitalChannelsSet = new Set<string>();
    private systemChannelsSet = new Set<string>();

    async updateChannelList(channelList) {
        this.updateChannelsLevels(channelList);
    }

    async updateChannel(channel) {
        if (isDigital(channel.fcuData.channelType)) {
            this.digitalChannelsSet.add(channel.rowId);
            this.systemChannelsSet.delete(channel.rowId);
        } else if (isSystemChannelType(channel.fcuData.channelType)) {
            this.systemChannelsSet.add(channel.rowId);
            this.digitalChannelsSet.delete(channel.rowId);
        } else {
            this.digitalChannelsSet.delete(channel.rowId);
            this.systemChannelsSet.delete(channel.rowId);
        }

        this.channelsLevels[channel.rowId] = {
            [FlagOption.DIGITAL]: channel.fcuData?.digitalAlarmSetting?.alarmLevel,
            [FlagOption.HIGH_HIGH]: channel.fcuData?.alarmSettingVeryHigh?.alarmLevel,
            [FlagOption.HIGH]: channel.fcuData?.alarmSettingHigh?.alarmLevel,
            [FlagOption.LOW]: channel.fcuData?.alarmSettingLow?.alarmLevel,
            [FlagOption.LOW_LOW]: channel.fcuData?.alarmSettingVeryLow?.alarmLevel,
            [FlagOption.FEEDBACK]: channel.fcuData?.alarmSettingFeedbackError?.alarmLevel,
            [FlagOption.SYSTEM_CHANNEL]: channel.fcuData?.systemAlarmSetting?.alarmLevel,
            groupId: channel.groupId,
        };
    }

    updateFlags(channelsData) {
        const updates = {};
        for (let i = 0; i < channelsData.length; i++) {
            updates[channelsData[i].id] = channelsData[i];
            this.updateStatus(channelsData[i]);
        }
        Object.assign(this.flagValues, updates);
    }

    private updateStatus(channel: ChannelFlags) {
        if (!channel || !channel.id) return;
        let flags = channel.flags;
        if (!flags?.alarm || !flags?.unAck) return;

        let alarmFlags = flags.alarm;
        let ackedFlags = flags.unAck;

        let activeAlarmFlag = this.getAlarmClass(alarmFlags);
        let activeAckFlag = this.getAlarmClass(ackedFlags);

        const id = channel.id;

        if (
            (activeAlarmFlag !== FlagOption.NORMAL || activeAckFlag !== FlagOption.NORMAL) &&
            this.digitalChannelsSet.has(channel.id)
        ) {
            this.channelStatus[id] = FlagOption.DIGITAL;
            this.updateLevels(id);
            return;
        }

        if (
            (activeAlarmFlag !== FlagOption.NORMAL || activeAckFlag !== FlagOption.NORMAL) &&
            this.systemChannelsSet.has(channel.id)
        ) {
            this.channelStatus[id] = FlagOption.SYSTEM_CHANNEL;
            this.updateLevels(id);
            return;
        }

        let newStatus: FlagOption | null = null;

        if (
            (activeAlarmFlag == FlagOption.HIGH_HIGH && activeAckFlag === FlagOption.HIGH) ||
            (activeAlarmFlag == FlagOption.LOW_LOW && activeAckFlag === FlagOption.LOW)
        ) {
            activeAlarmFlag = activeAckFlag;
        }

        if (
            (activeAlarmFlag === FlagOption.HIGH && activeAckFlag === FlagOption.HIGH_HIGH) ||
            (activeAlarmFlag === FlagOption.LOW && activeAckFlag === FlagOption.LOW_LOW) ||
            (activeAlarmFlag === FlagOption.NORMAL && activeAckFlag !== FlagOption.NORMAL)
        ) {
            newStatus = activeAckFlag;
        } else {
            switch (activeAlarmFlag) {
                case FlagOption.HIGH_HIGH:
                case FlagOption.LOW_LOW:
                case FlagOption.LOW:
                case FlagOption.HIGH:
                case FlagOption.FEEDBACK:
                case FlagOption.OVER:
                case FlagOption.UNDER:
                case FlagOption.NORMAL:
                case FlagOption.DIGITAL:
                    newStatus = activeAlarmFlag;
                    break;
                case FlagOption.SYSTEM_CHANNEL:
                    newStatus = activeAlarmFlag;
                    break;
                default:
                    newStatus = null;
            }
        }

        this.channelStatus[id] = newStatus;
        this.updateLevels(id);
    }

    getFlagsByRowId(channelId: string): ChannelFlags {
        return this.flagValues[channelId];
    }

    getLevelByRowId(channelId: string): AlarmLevel {
        return this.channelLevel[channelId];
    }

    getStatusByRowId(channelId: string): FlagOption {
        return this.channelStatus[channelId];
    }

    private updateChannelsLevels(channels) {
        if (!channels || channels.length == 0) {
            this.channelsLevels = {};
            return;
        }
        channels.forEach(channel => {
            if (!channel?.fcuData?.fcuChannelId) return;
            this.channelsLevels[channel.rowId] = {
                [FlagOption.HIGH_HIGH]: channel.fcuData?.alarmSettingVeryHigh?.alarmLevel,
                [FlagOption.HIGH]: channel.fcuData?.alarmSettingHigh?.alarmLevel,
                [FlagOption.LOW]: channel.fcuData?.alarmSettingLow?.alarmLevel,
                [FlagOption.LOW_LOW]: channel.fcuData?.alarmSettingVeryLow?.alarmLevel,
                [FlagOption.FEEDBACK]: channel.fcuData?.alarmSettingFeedbackError?.alarmLevel,
                [FlagOption.NORMAL]: AlarmLevel.None,
                groupId: channel.groupId,
            };
        });
    }

    private updateLevels(channelId) {
        let status = this.channelStatus[channelId];
        if (!status) return;
        let levels = this.channelsLevels[channelId];
        if (!levels) return;
        let channelLevel = levels[status];
        if (status == FlagOption.OVER || status == FlagOption.UNDER) channelLevel = SENSOR_FAILURE;
        this.channelLevel[channelId] = channelLevel;
    }

    private getAlarmClass(alarmFlag: AlarmFlags): FlagOption {
        if (alarmFlag.highHigh == 1) {
            return FlagOption.HIGH_HIGH;
        } else if (alarmFlag.lowlow == 1) {
            return FlagOption.LOW_LOW;
        } else if (alarmFlag.low == 1) {
            return FlagOption.LOW;
        } else if (alarmFlag.high == 1) {
            return FlagOption.HIGH;
        } else if (alarmFlag.feedbackError == 1) {
            return FlagOption.FEEDBACK;
        } else if (alarmFlag.over == 1) {
            return FlagOption.OVER;
        } else if (alarmFlag.under == 1) {
            return FlagOption.UNDER;
        } else {
            return FlagOption.NORMAL;
        }
    }
}
